interface Point {
  value: number;
  label: string;
}

interface ChartProps {
  points: Point[];
  unit?: string;
  height?: number;
}

/**
 * Jednoduchý SVG graf. Zámerne bez knižnice — knižnica na grafy je pri jednej
 * čiare zbytočná záťaž a drží prvotný bundle malý.
 */
export function Chart({ points, unit = '', height = 220 }: ChartProps) {
  const width = 720;
  const padding = { top: 16, right: 16, bottom: 28, left: 44 };

  if (points.length === 0) {
    return <p className="muted small">Zatiaľ tu nie sú žiadne merania. Dokonči aspoň jednu lekciu.</p>;
  }

  const values = points.map((p) => p.value);
  const maxValue = Math.max(...values, 10);
  const minValue = 0;
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const xFor = (index: number) =>
    padding.left + (points.length === 1 ? innerWidth / 2 : (index / (points.length - 1)) * innerWidth);
  const yFor = (value: number) =>
    padding.top + innerHeight - ((value - minValue) / (maxValue - minValue)) * innerHeight;

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(1)},${yFor(p.value).toFixed(1)}`).join(' ');

  const gridValues = [0, maxValue / 2, maxValue];

  return (
    <svg className="chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Graf vývoja: ${unit}`}>
      {gridValues.map((value) => (
        <g key={value}>
          <line className="grid-line" x1={padding.left} x2={width - padding.right} y1={yFor(value)} y2={yFor(value)} />
          <text x={padding.left - 8} y={yFor(value) + 4} textAnchor="end">
            {Math.round(value)}
          </text>
        </g>
      ))}

      <line className="axis" x1={padding.left} x2={width - padding.right} y1={yFor(0)} y2={yFor(0)} />

      {points.length > 1 && <path className="line" d={linePath} />}

      {points.map((point, index) => (
        <g key={`${point.label}-${index}`}>
          <circle className="dot" cx={xFor(index)} cy={yFor(point.value)} r={3.5} />
          <title>{`${point.label}: ${point.value} ${unit}`}</title>
        </g>
      ))}

      <text x={padding.left} y={height - 6}>{points[0].label}</text>
      <text x={width - padding.right} y={height - 6} textAnchor="end">
        {points[points.length - 1].label}
      </text>
      <text x={padding.left - 8} y={padding.top - 4} textAnchor="end">
        {unit}
      </text>
    </svg>
  );
}
